import { useNavigate } from 'react-router-dom';

/**
 * 🧭 NotificationNavigation - Utilitário para Navegação Direta
 * 
 * Responsabilidades:
 * - Mapear tipos de notificação para rotas específicas
 * - Implementar navegação inteligente baseada em dados
 * - Gerenciar parâmetros de URL e estados
 */

class NotificationNavigation {
  constructor() {
    this.navigate = null;
  }

  /**
   * 🔧 Inicializar com instância do navigate
   */
  initialize(navigate) {
    this.navigate = navigate;
  }

  /**
   * 🧭 Navegar baseado no tipo de notificação
   */
  navigateToNotification(notification) {
    if (!this.navigate) {
      console.error('❌ Navigation não inicializada');
      return;
    }

    const { type, data } = notification;

    try {
      // 🎯 Se a notificação tem redirectTo, usar diretamente
      if (data?.redirectTo) {
        this.navigate(data.redirectTo);
        return;
      }

      switch (type) {
        // 💰 Vendas
        case 'sale_processed':
        case 'sale_new':
          this.navigateToSale(notification, data);
          break;
          
        // 💸 Comissões
        case 'commission_received':
          this.navigateToCommission(notification, data);
          break;
          
        // 🤝 Afiliados - Solicitações
        case 'affiliate_signup':
        case 'affiliate_request':
          this.navigateToAffiliateRequest(notification, data);
          break;
          
        // ✅ Afiliados - Status
        case 'affiliate_approved':
        case 'affiliate_rejected':
          this.navigateToAffiliateStatus(notification, data);
          break;

        // 📨 Convite de Afiliado Pendente
        case 'affiliate_invite_pending':
          this.navigate('/Dashboard'); // Banner aparece no dashboard
          break;
          
        // 🎫 Tickets
        case 'ticket_replied':
          this.navigateToTicket(notification, data);
          break;
          
        // 💰 Saques
        case 'balance_available':
        case 'withdrawal_approved':
        case 'withdrawal_rejected':
          this.navigateToWithdrawals(notification, data);
          break;
          
        // 👤 Responsável Aprovado/Rejeitado
        case 'responsible_approved':
        case 'responsible_rejected':
          this.navigate('/responsibles');
          break;
          
        // 📦 Produto Aprovado/Rejeitado
        case 'product_approved':
        case 'product_rejected':
          this.navigate('/products/products');
          break;
          
        // 🏦 Conta Bancária Aprovada/Rejeitada
        case 'bank_account_approved':
        case 'bank_account_rejected':
          this.navigate('/banks');
          break;
          
        default:
          this.navigateToDefault(notification);
      }
    } catch (error) {
      console.error('❌ Erro na navegação:', error);
      this.navigateToDefault(notification);
    }
  }

  /**
   * 💰 Navegar para venda específica
   */
  navigateToSale(notification, data) {
    if (data?.saleId) {
      // Navegar para detalhes da venda
      this.navigate(`/sales-management?saleId=${data.saleId}`);
    } else if (data?.type === 'producer') {
      // Produtor: ir para vendas
      this.navigate('/sales-management');
    } else if (data?.type === 'affiliate') {
      // Afiliado: ir para comissões
      this.navigate('/commissions');
    } else {
      this.navigate('/sales-management');
    }
  }

  /**
   * 🎉 Navegar para comissão específica
   */
  navigateToCommission(notification, data) {
    if (data?.saleId) {
      // Navegar para comissões com filtro da venda
      this.navigate(`/commissions/dashboard?saleId=${data.saleId}`);
    } else {
      // Navegar para comissões gerais (dashboard)
      this.navigate('/commissions/dashboard');
    }
  }

  /**
   * 🤝 Navegar para solicitação de afiliação
   */
  navigateToAffiliateRequest(notification, data) {
    if (data?.affiliateId && data?.productName) {
      // Navegar para aprovações de afiliados
      this.navigate(`/admin/approvals?affiliateId=${data.affiliateId}&product=${encodeURIComponent(data.productName)}`);
    } else {
      // Navegar para aprovações gerais
      this.navigate('/admin/approvals');
    }
  }

  /**
   * ✅ Navegar para status de afiliação
   */
  navigateToAffiliateStatus(notification, data) {
    if (data?.productName) {
      // Navegar para meus afiliados com filtro do produto
      this.navigate(`/my-affiliates?product=${encodeURIComponent(data.productName)}`);
    } else {
      // Navegar para meus afiliados
      this.navigate('/my-affiliates');
    }
  }

  /**
   * 🎫 Navegar para ticket específico
   */
  navigateToTicket(notification, data) {
    if (data?.ticketId) {
      // Navegar para ticket específico
      this.navigate(`/tickets?ticketId=${data.ticketId}`);
    } else {
      // Navegar para lista de tickets
      this.navigate('/tickets');
    }
  }

  /**
   * 💰 Navegar para saques
   */
  navigateToWithdrawals(notification, data) {
    if (data?.withdrawalId) {
      // Navegar para saques com ID específico
      this.navigate(`/withdrawals?withdrawalId=${data.withdrawalId}`);
    } else {
      // Navegar para saques gerais
      this.navigate('/withdrawals');
    }
  }

  /**
   * 🏠 Navegação padrão (dashboard)
   */
  navigateToDefault(notification) {
    this.navigate('/Dashboard');
  }

  /**
   * 🔍 Obter URL de navegação sem navegar
   */
  getNavigationUrl(notification) {
    const { type, data } = notification;

    // 🎯 Se a notificação tem redirectTo, usar diretamente
    if (data?.redirectTo) {
      return data.redirectTo;
    }

    switch (type) {
      // 💰 Vendas
      case 'sale_processed':
      case 'sale_new':
        if (data?.saleId) {
          return `/sales-management?saleId=${data.saleId}`;
        }
        return '/sales-management';
        
      // 💸 Comissões
      case 'commission_received':
        if (data?.saleId) {
          return `/commissions/dashboard?saleId=${data.saleId}`;
        }
        return '/commissions/dashboard';
        
      // 🤝 Afiliados - Solicitações
      case 'affiliate_signup':
      case 'affiliate_request':
        if (data?.affiliateId && data?.productName) {
          return `/admin/approvals?affiliateId=${data.affiliateId}&product=${encodeURIComponent(data.productName)}`;
        }
        return '/my-affiliates';
        
      // ✅ Afiliados - Status
      case 'affiliate_approved':
      case 'affiliate_rejected':
        if (data?.productName) {
          return `/my-affiliates?product=${encodeURIComponent(data.productName)}`;
        }
        return '/my-affiliates';

      // 📨 Convite de Afiliado Pendente
      case 'affiliate_invite_pending':
        return '/Dashboard';
        
      // 🎫 Tickets
      case 'ticket_replied':
        if (data?.ticketId) {
          return `/tickets?ticketId=${data.ticketId}`;
        }
        return '/tickets';
        
      // 💰 Saques
      case 'balance_available':
      case 'withdrawal_approved':
      case 'withdrawal_rejected':
        if (data?.withdrawalId) {
          return `/withdrawals?withdrawalId=${data.withdrawalId}`;
        }
        return '/withdrawals';
        
      // 👤 Responsável Aprovado/Rejeitado
      case 'responsible_approved':
      case 'responsible_rejected':
        return '/responsibles';
        
      // 📦 Produto Aprovado/Rejeitado
      case 'product_approved':
      case 'product_rejected':
        return '/products/products';
        
      // 🏦 Conta Bancária Aprovada/Rejeitada
      case 'bank_account_approved':
      case 'bank_account_rejected':
        return '/banks';
        
      default:
        return '/Dashboard';
    }
  }

  /**
   * 📋 Obter ações disponíveis para notificação
   */
  getNotificationActions(notification) {
    const { type, data } = notification;
    const actions = [];

    // Ação principal baseada no tipo
    switch (type) {
      // 💰 Vendas
      case 'sale_processed':
      case 'sale_new':
        actions.push({
          label: 'Ver Venda',
          icon: 'mdi:eye',
          variant: 'primary',
          onClick: () => this.navigateToSale(notification, data)
        });
        break;
        
      // 💸 Comissões
      case 'commission_received':
        actions.push({
          label: 'Ver Comissão',
          icon: 'mdi:currency-brl',
          variant: 'success',
          onClick: () => this.navigateToCommission(notification, data)
        });
        break;
        
      // 🤝 Afiliados - Solicitações
      case 'affiliate_signup':
      case 'affiliate_request':
        actions.push({
          label: 'Aprovar Afiliado',
          icon: 'mdi:account-check',
          variant: 'primary',
          onClick: () => this.navigateToAffiliateRequest(notification, data)
        });
        break;
        
      // ✅ Afiliados - Aprovado
      case 'affiliate_approved':
        actions.push({
          label: 'Ver Afiliados',
          icon: 'mdi:account-group',
          variant: 'success',
          onClick: () => this.navigateToAffiliateStatus(notification, data)
        });
        break;
        
      // ❌ Afiliados - Rejeitado
      case 'affiliate_rejected':
        actions.push({
          label: 'Ver Afiliados',
          icon: 'mdi:account-group',
          variant: 'warning',
          onClick: () => this.navigateToAffiliateStatus(notification, data)
        });
        break;

      // 📨 Convite de Afiliado Pendente
      case 'affiliate_invite_pending':
        actions.push({
          label: 'Ver Convites',
          icon: 'mdi:account-plus',
          variant: 'primary',
          onClick: () => this.navigate('/Dashboard')
        });
        break;
        
      // 🎫 Tickets
      case 'ticket_replied':
        actions.push({
          label: 'Ver Ticket',
          icon: 'mdi:ticket-account',
          variant: 'info',
          onClick: () => this.navigateToTicket(notification, data)
        });
        break;
        
      // 💰 Saques
      case 'balance_available':
      case 'withdrawal_approved':
      case 'withdrawal_rejected':
        actions.push({
          label: 'Ver Saques',
          icon: 'mdi:bank-transfer',
          variant: 'success',
          onClick: () => this.navigateToWithdrawals(notification, data)
        });
        break;
        
      // 👤 Responsável Aprovado
      case 'responsible_approved':
        actions.push({
          label: 'Ver Responsáveis',
          icon: 'mdi:account-check',
          variant: 'success',
          onClick: () => this.navigate('/responsibles')
        });
        break;
        
      // ❌ Responsável Rejeitado
      case 'responsible_rejected':
        actions.push({
          label: 'Ver Detalhes',
          icon: 'mdi:account-alert',
          variant: 'warning',
          onClick: () => this.navigate('/responsibles')
        });
        break;
        
      // 📦 Produto Aprovado
      case 'product_approved':
        actions.push({
          label: 'Ver Produto',
          icon: 'mdi:package-check',
          variant: 'success',
          onClick: () => {
            if (data?.productUuid) {
              this.navigate(`/produtos/ver/${data.productUuid}`);
            } else {
              this.navigate('/produtos');
            }
          }
        });
        break;
        
      // ❌ Produto Rejeitado
      case 'product_rejected':
        actions.push({
          label: 'Ver Detalhes',
          icon: 'mdi:package-variant-closed',
          variant: 'warning',
          onClick: () => {
            if (data?.productUuid) {
              this.navigate(`/produtos/ver/${data.productUuid}`);
            } else {
              this.navigate('/produtos');
            }
          }
        });
        break;
        
      // 🏦 Conta Bancária Aprovada
      case 'bank_account_approved':
        actions.push({
          label: 'Ver Contas',
          icon: 'mdi:bank-check',
          variant: 'success',
          onClick: () => this.navigate('/banks')
        });
        break;
        
      // ❌ Conta Bancária Rejeitada
      case 'bank_account_rejected':
        actions.push({
          label: 'Ver Detalhes',
          icon: 'mdi:bank-remove',
          variant: 'warning',
          onClick: () => this.navigate('/banks')
        });
        break;
    }

    // Ação secundária: ir para dashboard
    actions.push({
      label: 'Dashboard',
      icon: 'mdi:view-dashboard',
      variant: 'outline-secondary',
      onClick: () => this.navigateToDefault(notification)
    });

    return actions;
  }
}

// Singleton instance
const notificationNavigation = new NotificationNavigation();

export default notificationNavigation;
